import { createElement } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import type { ProfileHostPorts } from '../../services/profileHost';
import { ProfileDialog } from './ProfileDialog';

export type ProfileModalResult = {
  success?: boolean;
  redirect?: boolean;
  redirectTo?: string;
};

export type ProfileModalHandle = {
  element: { modal: () => void };
  close: Promise<ProfileModalResult | undefined>;
};

export type OpenProfileModalPorts = {
  host: ProfileHostPorts;
  onClose?: (result: ProfileModalResult | undefined) => void;
  logError?: (error: unknown) => void;
  document?: Document;
};

const PROFILE_MODAL_ROOT = 'data-profile-modal-root';

function releaseModalOpen(doc: Document): void {
  if (doc.querySelector(`[${PROFILE_MODAL_ROOT}]`)) {
    return;
  }
  doc.body.classList.remove('modal-open');
}

function mountProfileDialog(
  ports: OpenProfileModalPorts,
  doc: Document
): ProfileModalHandle {
  const container = doc.createElement('div');
  container.setAttribute(PROFILE_MODAL_ROOT, 'true');
  doc.body.appendChild(container);
  doc.body.classList.add('modal-open');

  const root: Root = createRoot(container);
  let settled = false;
  let resolveClose: (result: ProfileModalResult | undefined) => void = () => undefined;
  const close = new Promise<ProfileModalResult | undefined>((resolve) => {
    resolveClose = resolve;
  });

  const finish = (result?: ProfileModalResult) => {
    if (settled) {
      return;
    }
    settled = true;
    const outcome = result ?? { success: false, redirect: false };
    root.unmount();
    container.remove();
    releaseModalOpen(doc);
    resolveClose(outcome);
    ports.onClose?.(outcome);
  };

  root.render(
    createElement(ProfileDialog, {
      data: {
        orchestrated: true,
        profileDialogEnabled: true,
        profilePageEnabled: true,
        embedInHostModal: false,
        host: ports.host,
        onDismiss: finish,
      },
    })
  );

  return {
    element: {
      modal() {
        return undefined;
      },
    },
    close,
  };
}

/**
 * Header and e-agreements open the profile here.
 * React owns `#profile-modal`; no Angular controller is constructed.
 */
export function openProfileModal(
  ports: OpenProfileModalPorts
): Promise<ProfileModalHandle | undefined> {
  try {
    return Promise.resolve(mountProfileDialog(ports, ports.document ?? document));
  } catch (error: unknown) {
    ports.logError?.(error);
    return Promise.resolve(undefined);
  }
}
